/**
 * AI Spotted on a question bank row.
 *
 * One question can carry several findings. The tag is one rollup. The list
 * names the mix. When more than one kind is open, the rollup is the most
 * urgent: a writing or format problem, then weak stats, then a review.
 * A clean question has no tag.
 */

import { pbiBand } from "@/lib/pbi-bands"
import { CS101_A5_USE_CASES } from "@/lib/question-quality/usecase-mapping"

export type LibraryAiSpotted =
  | "needs-fixing"
  | "worth-a-look"
  | "weak-performance"
  | "looks-good"

export const LIBRARY_AI_SPOTTED_LABEL: Record<LibraryAiSpotted, string> = {
  "needs-fixing": "Needs fixing",
  "worth-a-look": "Worth a look",
  "weak-performance": "Weak performance",
  "looks-good": "Looks good",
}

export const LIBRARY_AI_SPOTTED_PRESENTATION: Record<
  LibraryAiSpotted,
  { tone: "danger" | "info" | "warning" | "success"; icon: string }
> = {
  "needs-fixing": { tone: "danger", icon: "fa-circle-exclamation" },
  "worth-a-look": { tone: "info", icon: "fa-eye" },
  "weak-performance": { tone: "warning", icon: "fa-chart-line" },
  "looks-good": { tone: "success", icon: "fa-circle-check" },
}

/** Writing repair stays Looks good unless exam stats or the case still need review. */
export function settledWritingAiSpotted(
  fromStats: LibraryAiSpotted | null | undefined,
  caseTag?: LibraryAiSpotted | null,
): LibraryAiSpotted {
  if (fromStats === "weak-performance" || caseTag === "weak-performance") return "weak-performance"
  if (caseTag === "worth-a-look") return "worth-a-look"
  return "looks-good"
}

const BROKEN_CHOICE = /\b(?:all|none) of the above\b|\bstated (?:mechanism|fact)\b/i

/** Placeholder choices the preview invents when a question has no real key. */
export function choiceSetIsBroken(
  options: readonly { text?: string }[] | undefined,
): boolean {
  if (!options?.length) return false
  return options.some((option) => BROKEN_CHOICE.test(option.text ?? ""))
}

/**
 * Bank, preview, and editor share this tag.
 * Broken placeholder choices are Needs fixing.
 * Looks good does not hide a standing Worth a look or Weak performance case.
 */
function normalizeSpottedText(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase()
}

function contentWords(value: string): Set<string> {
  return new Set(
    value.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length > 4),
  )
}

/** Blank chips, underscores, and a dropped chip are the same stem, not a rewrite. */
function stemForCompare(value: string): string {
  return normalizeSpottedText(value)
    .replace(/\{\{\s*blank\s*\}\}/g, " ")
    .replace(/_{2,}/g, " ")
    .replace(/\bblank\s*\d+\b/g, " ")
    .replace(/\bblank\b/g, " ")
    .replace(/[{}]/g, " ")
    .replace(/[.…]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

/** A rewrite of this question, not a different stem pasted into the same id. */
function stemEditClearsFinding(stem: string, baseStem: string): boolean {
  if (stemForCompare(stem) === stemForCompare(baseStem)) return false
  const left = contentWords(stem)
  const right = contentWords(baseStem)
  let shared = 0
  for (const word of left) if (right.has(word)) shared += 1
  return shared >= 3
}

/** Case findings a saved stem or choice edit has already cleared. */
export function caseIssueIdsClearedByEdit(input: {
  id?: string
  stem?: string
  baseStem?: string
  choiceTexts?: readonly string[]
  baseChoiceTexts?: readonly string[]
}): string[] {
  const tag = libraryCaseTag(input.id)
  if (!tag) return []
  const stemChanged = Boolean(
    input.stem?.trim() &&
    input.baseStem?.trim() &&
    stemEditClearsFinding(input.stem, input.baseStem),
  )
  const current = (input.choiceTexts ?? []).map(normalizeSpottedText).filter(Boolean)
  const base = (input.baseChoiceTexts ?? []).map(normalizeSpottedText).filter(Boolean)
  const choicesChanged =
    current.length > 0 && (base.length === 0 || current.join("\n") !== base.join("\n"))
  return leoSpottedResolvedIssueIds(input.id, tag, {
    ...(stemChanged ? { stem: true } : {}),
    ...(choicesChanged ? { choices: true } : {}),
  })
}

export function reconcileAiSpotted(input: {
  id?: string
  aiSpotted?: LibraryAiSpotted | null
  options?: readonly { text?: string }[]
  resolvedIssueIds?: readonly string[]
}): LibraryAiSpotted | undefined {
  if (choiceSetIsBroken(input.options)) return "needs-fixing"
  const caseTag = libraryCaseTag(input.id)
  const resolved = new Set(input.resolvedIssueIds ?? [])
  if (caseTag && resolved.size > 0) {
    const open = issueDefsFor(input.id, caseTag).filter(
      (issue) => !isStatIssueLabel(issue) && !resolved.has(issue.id),
    )
    if (open.length === 0) {
      return caseTag === "weak-performance" ? "weak-performance" : "looks-good"
    }
  }
  if (caseTag === "worth-a-look" || caseTag === "weak-performance") return caseTag
  return input.aiSpotted ?? caseTag ?? undefined
}

/** Sentence for the Looks good info control. */
export function leoFixedSummary(changed: {
  stem?: boolean
  choices?: boolean
  rationale?: boolean
}): string {
  const parts = [
    changed.stem ? "the stem" : "",
    changed.choices ? "the answer choices" : "",
    changed.rationale ? "the rationale" : "",
  ].filter(Boolean)
  if (parts.length === 0) return "The open writing findings were updated."
  if (parts.length === 1) return `Leo updated ${parts[0]}.`
  if (parts.length === 2) return `Leo updated ${parts[0]} and ${parts[1]}.`
  return `Leo updated ${parts[0]}, ${parts[1]}, and ${parts[2]}.`
}

/**
 * Showcase rollup. Needs fixing: the question is not ready to use (stem,
 * choices, format, passage, rationale, or an empty draft). Worth a look: it
 * can be used as written, and should be opened before the next use (reuse,
 * a variation, missing classification, or discrimination that only separates
 * somewhat). Weak performance: exam stats say it is not doing its job, even
 * when a writing issue sits next to that.
 */
const CASE_TAG: Record<string, LibraryAiSpotted> = {
  q1: "needs-fixing",
  q2: "worth-a-look",
  q3: "needs-fixing",
  q4: "needs-fixing",
  q5: "needs-fixing",
  q6: "needs-fixing",
  q7: "needs-fixing",
  q8: "needs-fixing",
  q9: "needs-fixing",
  q10: "worth-a-look",
  q11: "needs-fixing",
  q12: "weak-performance",
  q13: "weak-performance",
  q14: "worth-a-look",
  q15: "needs-fixing",
  q16: "needs-fixing",
  q17: "worth-a-look",
  q18: "worth-a-look",
  q19: "weak-performance",
  q20: "worth-a-look",
  "q17-draft": "needs-fixing",
  q21: "needs-fixing",
  q22: "needs-fixing",
  q23: "needs-fixing",
  q24: "needs-fixing",
  q25: "needs-fixing",
  q26: "weak-performance",
  q27: "weak-performance",
}

export function aiSpottedForUseCase(id: string): LibraryAiSpotted {
  return CASE_TAG[id] ?? "needs-fixing"
}

export function libraryAiSpotted(input: {
  reviewResolved?: boolean
  needsAttention?: boolean
  /**
   * Omit when this row has no psychometric field. Pass `null` when it was
   * measured and the score is missing (`"--"`).
   */
  pbi?: number | null
}): LibraryAiSpotted | null {
  if (input.reviewResolved) return null
  if (input.needsAttention) return "needs-fixing"
  if (input.pbi === undefined) return null
  const band = pbiBand(input.pbi)
  if (band.key === "weak" || band.key === "none") return "weak-performance"
  if (band.key === "adequate") return "worth-a-look"
  return null
}

/** Every showcase case has a tag. */
export const AI_SPOTTED_CASE_IDS = CS101_A5_USE_CASES.map((row) => row.id)

export type LeoSpottedIssue = {
  id: string
  label: string
}

/** Leo spotted never names the question title. */
export function isLeoSpottedTitleFinding(issue: {
  id?: string
  label?: string
  cover?: string
}): boolean {
  if (issue.cover === "title") return true
  if (issue.id === "title" || (issue.id != null && /title/i.test(issue.id))) return true
  return /\btitle\b/i.test(issue.label ?? "")
}

export function withoutLeoSpottedTitleFindings<T extends {
  id?: string
  label?: string
  cover?: string
}>(issues: readonly T[]): T[] {
  return issues.filter((issue) => !isLeoSpottedTitleFinding(issue))
}

export type AiSpottedBrief = {
  why: string
  /** What Enhance with Leo will do, shown before the author starts it. */
  next: string
  ask: string
  /** Fields Leo spotted, shown as a list instead of the why sentence. */
  issues: readonly LeoSpottedIssue[]
}

type SpottedIssueDef = LeoSpottedIssue & {
  /** Repair fields that clear this row. Defaults to the issue id. */
  fixedBy: readonly string[]
}

function spottedIssue(
  id: string,
  label: string,
  fixedBy: readonly string[] = [id],
): SpottedIssueDef {
  return { id, label, fixedBy }
}

const CASE_ISSUES: Record<string, readonly SpottedIssueDef[]> = {
  q1: [
    spottedIssue("stem", "Stem is incomplete"),
    spottedIssue("choices", "Two choices rest on facts the stem never gives"),
  ],
  q2: [spottedIssue("stem", "Used on {uses} assessments already")],
  q3: [
    spottedIssue("stem", "Stem is incomplete"),
    spottedIssue("choices", "All of the above and None of the above both appear"),
    spottedIssue("rationale", "Rationale is missing"),
  ],
  q4: [spottedIssue("choices", "One choice rests on a fact the stem never gives")],
  q5: [spottedIssue("passage", "The passage the stem refers to is not attached", ["passage", "stem"])],
  q6: [spottedIssue("format", "The stem says select all that apply, but the format is single choice", ["format", "stem"])],
  q7: [spottedIssue("stem", "The stem ends with \"an\", so only one choice fits grammatically", ["stem", "choices"])],
  q8: [spottedIssue("choices", "The correct choice is much longer than the rest")],
  q9: [spottedIssue("stem", "The negative in the stem is not capitalised")],
  q10: [spottedIssue("stem", "The stem could be worded more precisely")],
  q11: [spottedIssue("format", "The stem asks for a match, but the format is not matching", ["format", "stem"])],
  q12: [
    spottedIssue("stem", "PBI {pbi}, so the stem is not separating strong from weak examinees"),
    spottedIssue("choices", "The distractors are too easy to rule out"),
  ],
  q13: [
    spottedIssue("difficulty", "PBI {pbi} is below the review floor of 0.15", ["difficulty", "stem"]),
  ],
  q14: [
    spottedIssue("stem", "The stem could be worded more precisely"),
    spottedIssue("choices", "The distractors could be stronger"),
  ],
  q15: [spottedIssue("format", "The stem says which of the following, but the format is true or false", ["format", "stem"])],
  q16: [spottedIssue("stem", "The prompt is long for the marks on offer")],
  q17: [
    spottedIssue("bloom", "Bloom's level is not set"),
    spottedIssue("difficulty", "Difficulty is not set"),
  ],
  q18: [
    spottedIssue("passage", "The passage could be clearer"),
    spottedIssue("stem", "The stem could be clearer"),
  ],
  q19: [spottedIssue("difficulty", "Tagged Easy, but only 28% answered it correctly")],
  q20: [spottedIssue("stem", "Used on {uses} assessments already")],
  "q17-draft": [spottedIssue("stem", "The stem is only the word Describe")],
  q21: [spottedIssue("choices", "The dosage ranges overlap, so two choices can both be right")],
  q22: [spottedIssue("choices", "Only one choice is an enzyme, which gives the answer away")],
  q23: [spottedIssue("stem", "The stem is too thin to say what is being tested")],
  q24: [
    spottedIssue("passage", "The case the stem refers to is not attached", ["passage", "stem"]),
    spottedIssue("choices", "Propranolol reads as an obvious trick once the case is written"),
  ],
  q25: [
    spottedIssue("passage", "The lab panel the stem refers to is not attached", ["passage", "stem"]),
    spottedIssue("stem", "The stem is incomplete"),
    spottedIssue("choices", "Two choices name the same disturbance"),
  ],
  q26: [
    spottedIssue("choices", "The keyed choice is contraindicated, and another choice is the recommended agent"),
  ],
  q27: [
    spottedIssue("difficulty", "PBI {pbi}, but the writing is sound, so the cause is likely curriculum or exam position"),
  ],
}

const TAG_ISSUES: Record<LibraryAiSpotted, readonly SpottedIssueDef[]> = {
  "needs-fixing": [
    spottedIssue("stem", "A writing or format problem is still open", ["stem", "choices", "rationale", "format", "passage"]),
  ],
  "worth-a-look": [
    spottedIssue(
      "stem",
      "This question can be used as written. Review it before the next use",
    ),
  ],
  "weak-performance": [
    spottedIssue("difficulty", "Exam stats say this question is not doing its job", ["difficulty", "stem", "choices"]),
  ],
  "looks-good": [],
}

export type LeoSpottedChoice = {
  id: string
  label: string
  instruction: string
  /** Issue ids this choice would change. A finished id drops the choice. */
  covers?: readonly string[]
}

/** Asked before a rewrite when more than one change would be right. */
export type LeoSpottedClarify = {
  question: string
  choices: readonly LeoSpottedChoice[]
}

const CASE_BRIEF: Record<string, Omit<AiSpottedBrief, "issues">> = {
  q1: {
    why: "The stem is incomplete, and two choices refer to facts the stem never states.",
    next: "Leo will rewrite the stem and the two choices.",
    ask: "Fix the stem and the two distractors.",
  },
  q2: {
    why: "This question is sound, and it has been used 3 times in the course. Returning students may recognize the wording.",
    next: "Leo will ask whether to change the wording or write a new question with the same core.",
    ask: "Small wording change.",
  },
  q3: {
    why: "The stem is incomplete. All of the above and None of the above appear together, and there is no rationale.",
    next: "Leo will fix the stem, the choices, and the rationale.",
    ask: "Fix the stem, the distractors, and the rationale.",
  },
  q4: {
    why: "One choice refers to something the stem never states.",
    next: "Leo will replace the choice the stem never states.",
    ask: "Replace the distractor the stem never states.",
  },
  q5: {
    why: "The stem says to read a passage, and no passage is attached.",
    next: "Leo will ask how to handle the missing passage.",
    ask: "Add the missing passage.",
  },
  q6: {
    why: "The stem says select all that apply, and the format is single choice.",
    next: "Leo will ask whether to change the format or the stem.",
    ask: "Change this question to select all that apply.",
  },
  q7: {
    why: "The stem ends with an, and only the correct answer fits that grammar.",
    next: "Leo will rewrite the stem so it does not cue the answer.",
    ask: "Rewrite the stem so it does not cue the answer.",
  },
  q8: {
    why: "The correct choice is much longer than every distractor.",
    next: "Leo will balance the length of the correct choice.",
    ask: "Balance the length of the correct choice.",
  },
  q9: {
    why: "The stem says not, and NOT or EXCEPT is not marked.",
    next: "Leo will mark NOT or EXCEPT in the stem.",
    ask: "Mark NOT or EXCEPT in the stem.",
  },
  q10: {
    why: "This fill in the blank is clean. Another stem wording would help.",
    next: "Leo will offer another stem for this blank.",
    ask: "Give me another stem for this blank.",
  },
  q11: {
    why: "The stem says to match the disease states, and the format does not match.",
    next: "Leo will ask whether to change the format or the stem.",
    ask: "Change the format to match the stem.",
  },
  q12: {
    why: "Discrimination 0.32 is weak on this question.",
    next: "Leo will offer another wording and new choices.",
    ask: "See a different variation and swap the distractors.",
  },
  q13: {
    why: "PBI 0.12 is below the review floor.",
    next: "Leo will review the stem and choices against this result.",
    ask: "Review the stem and choices.",
  },
  q14: {
    why: "This question is sound. Another wording with new choices would help.",
    next: "Leo will rewrite the stem and the choices.",
    ask: "Rewrite the stem and the distractors.",
  },
  q15: {
    why: "The stem says which of the following, and the format is true or false.",
    next: "Leo will ask whether to change the format or the stem.",
    ask: "Change the format so it matches the stem.",
  },
  q16: {
    why: "The essay is long.",
    next: "Leo will offer a shorter essay.",
    ask: "Shorten the essay.",
  },
  q17: {
    why: "Bloom's and difficulty are unset.",
    next: "Leo will set Bloom's level and difficulty.",
    ask: "Set Bloom's and difficulty.",
  },
  q18: {
    why: "A passage is attached. The reference and the stem can both change.",
    next: "Leo will swap the passage and offer another stem.",
    ask: "Swap the passage and give me another stem.",
  },
  q19: {
    why: "This is tagged Easy, and examinees scored 28% correct.",
    next: "Leo will set the difficulty to hard.",
    ask: "Set the difficulty to hard.",
  },
  q20: {
    why: "This essay is clean. A variation would help, and no field is named.",
    next: "Leo will ask what you want changed.",
    ask: "Give me a variation of this essay.",
  },
  "q17-draft": {
    why: "The stem is only Describe.",
    next: "Leo will give this draft a full stem.",
    ask: "Give this draft a full stem.",
  },
  q21: {
    why: "10 to 20 mg overlaps the keyed 10 mg, so the dosage sequence is no longer exclusive.",
    next: "Leo will rebalance the dosages so they no longer overlap.",
    ask: "Rebalance the dosages so they no longer overlap.",
  },
  q22: {
    why: "The correct choice is an enzyme. The other choices mix diet, location, and a drug.",
    next: "Leo will replace those choices with enzymes from the same pathway.",
    ask: "Replace the other choices with enzymes from the same pathway.",
  },
  q23: {
    why: "The stem is only Shock in pediatric patients. The choices mix types, a fluid bolus, and a finding.",
    next: "Leo will ask which competency you are testing, then draft the stem and choices for that competency.",
    ask: "Ask which competency this question tests.",
  },
  q24: {
    why: "The stem points at a case that is not attached. A case of severe asthma would make Propranolol an obvious trick.",
    next: "Leo will add the case and replace Propranolol.",
    ask: "Add the missing case and replace Propranolol.",
  },
  q25: {
    why: "The stem is incomplete, the panel is missing, and two choices name the same disturbance.",
    next: "Leo will add the panel, finish the stem, and separate the choices.",
    ask: "Add the panel, finish the stem, and separate the choices.",
  },
  q26: {
    why: "PBI is -0.08. Diltiazem is keyed, and it is contraindicated in HFrEF. Metoprolol succinate is the recommended agent.",
    next: "Leo will re-key the recommended agent, or replace that choice, or retire the question.",
    ask: "Re-key the correct answer to metoprolol succinate.",
  },
  q27: {
    why: "The stem and choices are sound. PBI is 0.04, which points to the curriculum, similar names, or fatigue at the end of a long exam.",
    next: "Leo will add a rationale students can read after the exam.",
    ask: "Add a rationale for after the exam.",
  },
}

/**
 * Asks before a rewrite. A choice exists only when it names a different outcome.
 * Framework: docs/exxat-ds/leo-clarify-framework.md
 */
const CASE_CLARIFY: Record<string, LeoSpottedClarify> = {
  q1: {
    question:
      "The stem is incomplete, and two choices do not belong. Which fix do you want?",
    choices: [
      {
        id: "both",
        label: "Rewrite the stem and replace those two choices",
        instruction: "Rewrite the stem and replace those two choices.",
        covers: ["stem", "choices"],
      },
      {
        id: "stem",
        label: "Rewrite the stem only",
        instruction: "Rewrite the stem only.",
        covers: ["stem"],
      },
      {
        id: "choices",
        label: "Replace those two choices only",
        instruction: "Replace those two choices only.",
        covers: ["choices"],
      },
    ],
  },
  q3: {
    question:
      "The stem is incomplete, All of the above and None of the above appear together, and there is no rationale. Which fix do you want?",
    choices: [
      {
        id: "all",
        label: "Fix the stem, the choices, and add a rationale",
        instruction: "Fix the stem, the choices, and add a rationale.",
        covers: ["stem", "choices", "rationale"],
      },
      {
        id: "stem-choices",
        label: "Fix the stem and the choices",
        instruction: "Fix the stem and the choices only.",
        covers: ["stem", "choices"],
      },
      {
        id: "aota",
        label: "Replace All of the above and None of the above",
        instruction: "Replace All of the above and None of the above.",
        covers: ["choices"],
      },
    ],
  },
  q4: {
    question: "One choice refers to something the stem never states. Which fix do you want?",
    choices: [
      {
        id: "choice",
        label: "Replace that one choice",
        instruction: "Replace the one choice the stem never states.",
      },
      {
        id: "both",
        label: "Replace that choice and complete the stem",
        instruction: "Replace that choice and complete the stem.",
      },
    ],
  },
  q7: {
    question:
      "The stem ends with an, so only the correct answer fits. Which fix do you want?",
    choices: [
      {
        id: "stem",
        label: "Rewrite the stem as a complete question",
        instruction: "Rewrite the stem so it does not cue the answer.",
      },
      {
        id: "choices",
        label: "Keep the stem and make every choice fit",
        instruction: "Keep the stem and make every choice fit the grammar.",
      },
    ],
  },
  q8: {
    question: "The correct choice is much longer than the others. Which fix do you want?",
    choices: [
      {
        id: "shorten",
        label: "Shorten the correct choice",
        instruction: "Shorten the correct choice so the lengths match.",
      },
      {
        id: "lengthen",
        label: "Lengthen the other choices",
        instruction: "Lengthen the other choices so they match the correct one.",
      },
    ],
  },
  q10: {
    question: "This fill in the blank is clean. Which wording do you want?",
    choices: [
      {
        id: "stem",
        label: "Another stem for this blank",
        instruction: "Give me another stem for this blank.",
      },
      {
        id: "harder",
        label: "A harder stem for this blank",
        instruction: "Give me a harder stem for this blank.",
      },
    ],
  },
  q12: {
    question: "Discrimination 0.32 is weak. Which change do you want?",
    choices: [
      {
        id: "both",
        label: "A new wording and new choices",
        instruction: "Give me a new wording and new choices.",
        covers: ["stem", "choices"],
      },
      {
        id: "stem",
        label: "A new wording only",
        instruction: "Give me a new wording only.",
        covers: ["stem"],
      },
      {
        id: "choices",
        label: "New choices only",
        instruction: "Give me new choices only.",
        covers: ["choices"],
      },
    ],
  },
  q13: {
    question: "PBI 0.12 is below the review floor. Which fix do you want?",
    choices: [
      {
        id: "stem",
        label: "Tighten the stem",
        instruction: "Tighten the stem.",
        covers: ["stem"],
      },
      {
        id: "both",
        label: "Tighten the stem and review the choices",
        instruction: "Tighten the stem and review the choices.",
        covers: ["stem", "difficulty"],
      },
    ],
  },
  q14: {
    question: "This question is sound. Which variation do you want?",
    choices: [
      {
        id: "both",
        label: "Rewrite the stem and the choices",
        instruction: "Rewrite the stem and the choices.",
        covers: ["stem", "choices"],
      },
      {
        id: "stem",
        label: "Rewrite the stem only",
        instruction: "Rewrite the stem only.",
        covers: ["stem"],
      },
      {
        id: "choices",
        label: "Rewrite the choices only",
        instruction: "Rewrite the choices only.",
        covers: ["choices"],
      },
    ],
  },
  q16: {
    question: "The essay is long. Which fix do you want?",
    choices: [
      {
        id: "stem",
        label: "Shorten the essay",
        instruction: "Shorten the essay.",
        covers: ["stem"],
      },
      {
        id: "keep",
        label: "Leave the essay as written",
        instruction: "Leave the essay as written.",
        covers: ["stem"],
      },
    ],
  },
  q17: {
    question: "Bloom's and difficulty are unset. Which should Leo set?",
    choices: [
      {
        id: "all",
        label: "Bloom's and difficulty",
        instruction: "Set Bloom's and difficulty.",
        covers: ["bloom", "difficulty"],
      },
      {
        id: "bloom",
        label: "Bloom's only",
        instruction: "Set Bloom's only.",
        covers: ["bloom"],
      },
      {
        id: "difficulty",
        label: "Difficulty only",
        instruction: "Set difficulty only.",
        covers: ["difficulty"],
      },
    ],
  },
  q18: {
    question: "A passage is attached. What should Leo swap?",
    choices: [
      {
        id: "both",
        label: "The passage and the stem",
        instruction: "Swap the passage and the stem.",
        covers: ["passage", "stem"],
      },
      {
        id: "passage",
        label: "The passage only",
        instruction: "Swap the passage only.",
        covers: ["passage"],
      },
      {
        id: "stem",
        label: "The stem only",
        instruction: "Rewrite the stem only.",
        covers: ["stem"],
      },
    ],
  },
  q19: {
    question: "This is tagged Easy, and examinees scored 28% correct. Which fix do you want?",
    choices: [
      {
        id: "both",
        label: "Set difficulty to hard and tighten the stem",
        instruction: "Set the difficulty to hard and tighten the stem.",
      },
      {
        id: "difficulty",
        label: "Set the difficulty to hard",
        instruction: "Set the difficulty to hard only.",
      },
      {
        id: "variation",
        label: "A harder variation of the question",
        instruction: "Give me a harder variation.",
      },
    ],
  },
  "q17-draft": {
    question: "The stem is only Describe. Which fix do you want?",
    choices: [
      {
        id: "stem",
        label: "A full stem",
        instruction: "Give this draft a full stem.",
        covers: ["stem"],
      },
    ],
  },
  q21: {
    question: "10 to 20 mg overlaps 10 mg. How should Leo fix the dosages?",
    choices: [
      {
        id: "rebalance",
        label: "Rebalance the full dosage sequence",
        instruction: "Rebalance the dosages so they no longer overlap.",
        covers: ["choices"],
      },
    ],
  },
  q22: {
    question: "The other choices are not enzymes. How should Leo replace them?",
    choices: [
      {
        id: "pathway",
        label: "Enzymes from the same pathway",
        instruction: "Replace the other choices with enzymes from the same pathway.",
        covers: ["choices"],
      },
    ],
  },
  q23: {
    question: "The stem is too thin. What competency are you testing?",
    choices: [
      {
        id: "fluid",
        label: "Initial fluid resuscitation",
        instruction: "Test initial fluid resuscitation in pediatric shock.",
        covers: ["stem", "choices"],
      },
      {
        id: "types",
        label: "Differentiating types of pediatric shock",
        instruction: "Test differentiating types of pediatric shock.",
        covers: ["stem", "choices"],
      },
      {
        id: "compensation",
        label: "Decompensated and compensated shock",
        instruction: "Test recognizing decompensated and compensated shock.",
        covers: ["stem", "choices"],
      },
    ],
  },
  q24: {
    question: "The case is missing, and writing it changes one choice. Which fix do you want?",
    choices: [
      {
        id: "both",
        label: "Add the case and replace Propranolol",
        instruction: "Add the missing case and replace Propranolol.",
        covers: ["passage", "choices"],
      },
      {
        id: "passage",
        label: "Add the case only",
        instruction: "Add the missing case only.",
        covers: ["passage"],
      },
    ],
  },
  q25: {
    question: "The panel is missing, the stem is incomplete, and two choices overlap. Which fix do you want?",
    choices: [
      {
        id: "all",
        label: "Panel, stem, and choices",
        instruction: "Add the panel, finish the stem, and separate the choices.",
        covers: ["passage", "stem", "choices"],
      },
      {
        id: "passage",
        label: "Add the panel first",
        instruction: "Add the missing panel only.",
        covers: ["passage"],
      },
      {
        id: "stem",
        label: "Finish the stem first",
        instruction: "Finish the stem only.",
        covers: ["stem"],
      },
    ],
  },
  q26: {
    question: "High scorers chose metoprolol succinate, and diltiazem is keyed. Which fix do you want?",
    choices: [
      {
        id: "rekey",
        label: "Re-key the correct answer to metoprolol succinate",
        instruction: "Re-key the correct answer to metoprolol succinate.",
        covers: ["choices"],
      },
      {
        id: "rewrite",
        label: "Replace metoprolol succinate so diltiazem stays keyed",
        instruction: "Replace the competing choice and keep diltiazem keyed.",
        covers: ["choices"],
      },
      {
        id: "retire",
        label: "Retire this question",
        instruction: "Retire this question.",
        covers: ["stem"],
      },
    ],
  },
  q27: {
    question: "The writing is sound. The low result is not in the stem. Which fix do you want?",
    choices: [
      {
        id: "rationale",
        label: "Add a rationale for after the exam",
        instruction: "Add a rationale for after the exam.",
        covers: ["rationale"],
      },
    ],
  },
  q2: {
    question: "This question has been used 3 times. How much should change?",
    choices: [
      {
        id: "wording",
        label: "Small wording change",
        instruction: "Small wording change. Keep the same answer.",
      },
      {
        id: "core",
        label: "Completely new question with the same core",
        instruction: "Completely new question with the same core.",
      },
    ],
  },
  q5: {
    question:
      "The stem asks students to read a passage, and none is attached. What should Leo do?",
    choices: [
      {
        id: "rewrite-stem",
        label: "Rewrite the stem without a passage",
        instruction: "Rewrite the stem so it does not ask students to read a passage.",
      },
      {
        id: "wait",
        label: "Leave it. I will attach a passage",
        instruction: "Leave this question unchanged. I will attach a passage.",
      },
      {
        id: "attach",
        label: "Attach the suggested passage",
        instruction: "Attach the suggested passage.",
      },
    ],
  },
  q9: {
    question: "The stem says not, and NOT or EXCEPT is not marked. What should Leo do?",
    choices: [
      {
        id: "mark",
        label: "Mark NOT in the stem",
        instruction: "Mark NOT or EXCEPT in the stem.",
      },
      {
        id: "rewrite",
        label: "Rewrite the stem without a negative",
        instruction: "Rewrite the stem so it does not use a negative.",
      },
    ],
  },
  q6: {
    question:
      "The stem says select all that apply, and the format is single choice. Which should change?",
    choices: [
      {
        id: "format",
        label: "Change the format to select all that apply",
        instruction: "Change the format to select all that apply so it matches the stem.",
      },
      {
        id: "stem",
        label: "Rewrite the stem as one best answer",
        instruction: "Rewrite the stem as one best answer so it matches single choice.",
      },
    ],
  },
  q11: {
    question:
      "The stem says to match the disease states, and the format does not match. Which should change?",
    choices: [
      {
        id: "format",
        label: "Change the format to a matching question",
        instruction: "Change the format to matching so it matches the stem.",
      },
      {
        id: "stem",
        label: "Rewrite the stem as one best answer",
        instruction: "Rewrite the stem as one best answer so it matches single choice.",
      },
    ],
  },
  q15: {
    question:
      "The stem says which of the following, and the format is true or false. Which should change?",
    choices: [
      {
        id: "format",
        label: "Change the format to single choice",
        instruction: "Change the format to single choice so it matches the stem.",
      },
      {
        id: "stem",
        label: "Rewrite the stem as true or false",
        instruction: "Rewrite the stem as true or false.",
      },
    ],
  },
  q20: {
    question: "What should Leo change?",
    choices: [
      {
        id: "stem",
        label: "The stem only",
        instruction: "Rewrite the stem. Keep the choices.",
      },
      {
        id: "whole",
        label: "The whole question",
        instruction: "Give me a variation of this question.",
      },
      {
        id: "difficulty",
        label: "The difficulty",
        instruction: "Give me a harder variation.",
      },
    ],
  },
}

const TAG_BRIEF: Record<LibraryAiSpotted, Omit<AiSpottedBrief, "issues">> = {
  "needs-fixing": {
    why: "A writing or format problem is still open. Fix it before this question is used.",
    next: "Leo will repair the open problem.",
    ask: "Fix the open quality problem.",
  },
  "worth-a-look": {
    why: "This question can be used as written. Review it before the next use for reuse, a variation, missing classification, or discrimination that only separates somewhat.",
    next: "Leo will review this question with you.",
    ask: "Review this question with Leo.",
  },
  "weak-performance": {
    why: "Exam stats say this question is not separating examinees, or the difficulty tag does not match how students scored.",
    next: "Leo will review how this question performed.",
    ask: "Review the performance of this question.",
  },
  "looks-good": {
    why: "Nothing else needs a change.",
    next: "",
    ask: "Create a new question with Leo.",
  },
}

export function libraryCaseTag(id: string | undefined): LibraryAiSpotted | null {
  const key = caseKey(id)
  return (key && CASE_TAG[key]) || null
}

function caseKey(id: string | undefined): string {
  if (!id) return ""
  return id.startsWith("ai-case-") ? id.slice("ai-case-".length) : id
}

/** Counts and PBI baked into the showcase, used when a caller has no row stats. */
const CASE_ISSUE_STATS: Record<string, { timesUsed?: number; pbi?: number }> = {
  q2: { timesUsed: 3 },
  q12: { pbi: 0.22 },
  q13: { pbi: 0.12 },
  q26: { pbi: -0.08 },
  q27: { pbi: 0.04 },
  q19: { pbi: 0.2 },
  q20: { timesUsed: 5 },
}

function fillSpottedLabel(
  label: string,
  key: string,
  stats?: { timesUsed?: number; pbi?: number | null },
): string {
  const fallback = CASE_ISSUE_STATS[key]
  const uses = stats?.timesUsed ?? fallback?.timesUsed
  const pbi = stats?.pbi ?? fallback?.pbi
  return label
    .replaceAll("{uses}", uses != null && uses > 0 ? String(uses) : "several")
    .replaceAll("{pbi}", pbi != null && Number.isFinite(Number(pbi)) ? Number(pbi).toFixed(2) : "")
}

export function leoSpottedClarify(id: string | undefined): LeoSpottedClarify | null {
  const key = caseKey(id)
  const specific = (key && CASE_CLARIFY[key]) || null
  if (!specific) return null
  const choices = specific.choices.filter(
    (choice) => !isLeoSpottedTitleFinding({ id: choice.id, label: `${choice.label} ${choice.instruction}` }),
  )
  if (choices.length === 0) return null
  return { question: specific.question, choices }
}

function isStatIssueLabel(issue: { id: string; label: string }): boolean {
  return issue.id === "pbi-review" || /\bPBI\b/.test(issue.label)
}

function lowerFirst(value: string): string {
  const second = value.charAt(1)
  if (second && second === second.toUpperCase() && second !== second.toLowerCase()) return value
  return value.charAt(0).toLowerCase() + value.slice(1)
}

export function questionForOpenIssues(labels: readonly string[]): string {
  if (labels.length === 0) return "How should Leo change this question?"
  if (labels.length === 1) return `${labels[0]}. Which fix do you want?`
  const head = labels.slice(0, -1).join(", ")
  const tail = lowerFirst(labels[labels.length - 1] ?? "")
  return `${head}, and ${tail}. Which fix do you want?`
}

/** Drops a choice that would change an issue that is already done. */
function choicesForOpenIssues(
  choices: readonly LeoSpottedChoice[],
  openIds: ReadonlySet<string>,
  knownIds: ReadonlySet<string>,
): LeoSpottedChoice[] {
  return choices.filter((choice) => {
    if (!choice.covers?.length) return true
    const known = choice.covers.filter((id) => knownIds.has(id))
    if (known.length === 0) return true
    return known.every((id) => openIds.has(id))
  })
}

/** Every Enhance opens an ask. A fork uses its own question. Otherwise Leo offers the recommended change and a free description. Finished issues are left out of the ask. */
export function leoEnhancePrompt(input: {
  id?: string
  tag: LibraryAiSpotted
  openIssues?: readonly { id: string; label: string }[]
}): LeoSpottedClarify {
  const specific = leoSpottedClarify(input.id)
  if (!specific) {
    const brief = CASE_BRIEF[caseKey(input.id)] || TAG_BRIEF[input.tag]
    return {
      question: "How should Leo change this question?",
      choices: [
        {
          id: "recommended",
          label: brief.ask,
          instruction: brief.ask,
        },
      ],
    }
  }
  if (!input.openIssues) return specific
  const knownIds = new Set(issueDefsFor(input.id, input.tag).map((issue) => issue.id))
  const openIds = new Set(input.openIssues.map((issue) => issue.id))
  const choices = choicesForOpenIssues(specific.choices, openIds, knownIds)
  if (choices.length === specific.choices.length) return specific
  const labels = input.openIssues.filter((issue) => !isStatIssueLabel(issue)).map((issue) => issue.label)
  if (choices.length === 0) {
    return {
      question: questionForOpenIssues(labels),
      choices: [
        {
          id: "remaining",
          label: labels.length === 1 ? labels[0]! : "Fix what is still open",
          instruction: labels.length === 1 ? labels[0]! : `Fix what is still open: ${labels.join(", ")}.`,
        },
      ],
    }
  }
  return { question: questionForOpenIssues(labels), choices }
}

function issueDefsFor(id: string | undefined, tag: LibraryAiSpotted): readonly SpottedIssueDef[] {
  return CASE_ISSUES[caseKey(id)] ?? TAG_ISSUES[tag]
}

/** Fields a repair actually changed. */
export type LeoSpottedTouch = {
  stem?: unknown
  choices?: unknown
  pairs?: unknown
  fillBlank?: unknown
  rationale?: unknown
  passage?: unknown
  type?: unknown
  title?: unknown
  bloom?: unknown
  difficulty?: unknown
}

function touchedIssueIds(touch: LeoSpottedTouch): Set<string> {
  const ids = new Set<string>()
  if (touch.stem) ids.add("stem")
  if (touch.choices || touch.pairs || touch.fillBlank) ids.add("choices")
  if (touch.rationale) ids.add("rationale")
  if (touch.passage) ids.add("passage")
  if (touch.type) ids.add("format")
  if (touch.title) ids.add("title")
  if (touch.bloom) ids.add("bloom")
  if (touch.difficulty) ids.add("difficulty")
  return ids
}

/** Issue ids this repair clears. A stem-only rewrite does not clear Choices. */
export function leoSpottedResolvedIssueIds(
  id: string | undefined,
  tag: LibraryAiSpotted,
  touch: LeoSpottedTouch | null,
): string[] {
  if (!touch) return []
  const changed = touchedIssueIds(touch)
  if (changed.size === 0) return []
  return issueDefsFor(id, tag)
    .filter((issue) => !/\bPBI\b/.test(issue.label))
    .filter((issue) => issue.fixedBy.length > 0 && issue.fixedBy.some((field) => changed.has(field)))
    .map((issue) => issue.id)
}

export function aiSpottedBrief(input: {
  id?: string
  tag: LibraryAiSpotted
  title?: string
  timesUsed?: number
  pbi?: number | null
}): AiSpottedBrief {
  const key = caseKey(input.id)
  const brief = CASE_BRIEF[key] || TAG_BRIEF[input.tag]
  const name = input.title?.trim()
  const issues = withoutLeoSpottedTitleFindings(
    issueDefsFor(input.id, input.tag).map(({ id, label }) => ({
      id,
      label: fillSpottedLabel(label, key, {
        timesUsed: input.timesUsed,
        pbi: input.pbi,
      }),
    })),
  )
  return {
    why: brief.why,
    next: brief.next,
    ask: name ? `${brief.ask} Question: ${name}.` : brief.ask,
    issues: withPbiReview(issues, input.pbi),
  }
}

/** A low PBI stays on the card after a rewrite. The stat does not move when the wording does. */
function withPbiReview(
  issues: readonly LeoSpottedIssue[],
  pbi: number | null | undefined,
): LeoSpottedIssue[] {
  if (issues.some((issue) => /\bPBI\b/.test(issue.label))) return [...issues]
  if (pbi == null || !Number.isFinite(Number(pbi))) return [...issues]
  if (pbiBand(pbi).key !== "weak") return [...issues]
  return [
    ...issues,
    {
      id: "pbi-review",
      label: `PBI ${Number(pbi).toFixed(2)} is below the review floor of 0.15`,
    },
  ]
}

const PLACEHOLDER_CHOICE_ISSUES: LeoSpottedIssue[] = [
  { id: "choices-aota", label: "All of the above and None of the above both appear" },
  { id: "choices-nota", label: "None of the above is the correct answer" },
]

/**
 * One spotted result for a catalog question.
 * The bank column, the preview sheet, and the editor all call this.
 * A category line appears only when the question is explicitly unassigned.
 */
export function openQuestionSpotted(input: {
  id?: string
  stem?: string
  baseStem?: string
  choiceTexts?: readonly string[]
  baseChoiceTexts?: readonly string[]
  resolvedIssueIds?: readonly string[]
  categoryUnassigned?: boolean
  categoryIds?: readonly string[] | null
  options?: readonly { text?: string }[]
  pbi?: number | null
  timesUsed?: number
}): {
  tag: LibraryAiSpotted
  why: string
  issues: LeoSpottedIssue[]
} | null {
  const caseTag = libraryCaseTag(input.id)
  if (!caseTag) return null
  if (choiceSetIsBroken(input.options)) {
    return {
      tag: "needs-fixing",
      why: "The answer choices are still placeholders.",
      issues: PLACEHOLDER_CHOICE_ISSUES,
    }
  }
  const resolved = new Set([
    ...(input.resolvedIssueIds ?? []),
    ...caseIssueIdsClearedByEdit(input),
  ])
  const brief = aiSpottedBrief({
    id: input.id,
    tag: caseTag,
    timesUsed: input.timesUsed,
    pbi: input.pbi,
  })
  const issues = brief.issues.filter((issue) => !resolved.has(issue.id))
  const assigned = input.categoryIds?.some((id) => id.trim())
  if (
    input.categoryUnassigned &&
    !assigned &&
    !issues.some((issue) => /category is missing/i.test(issue.label))
  ) {
    issues.push({ id: "classification-category", label: "Category is missing" })
  }
  const categoryMissing = issues.some((issue) => /category is missing/i.test(issue.label))
  const tag: LibraryAiSpotted = categoryMissing
    ? "needs-fixing"
    : issues.length === 0
      ? caseTag === "weak-performance"
        ? "weak-performance"
        : "looks-good"
      : caseTag
  return {
    tag,
    why: issues.length === 0
      ? "Nothing else needs a change."
      : issues.map((issue) => issue.label).join(" "),
    issues,
  }
}
